import { doesUserHaveReleaseToggle } from '@skyspecs/release-utils-client';
import { useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Spinner, useMessage } from 'components/ui';
import { useApolloContext } from 'utils/apollo';
import { notifyBugsnag } from 'utils/bugsnag';
import { setTitle } from 'utils/document';
import { setHeapOrganization } from 'utils/heap';
import { removeItem } from 'utils/storage';
import { type Customer, type User } from 'utils/types';

import {
  type AtlasGqlOrgFragmentFragment,
  useGetFeatureFlagsQuery,
  useUserDetailsQuery,
} from '../../types/atlas-graphql';
import { CI_MERGE_REQUEST_IID, DEPLOY_ENV } from '../env';

import { AccountContext } from './AccountContext';
import { activeCustomerAtom, currentCustomerAtom, featuresAtom, releaseTogglesAtom } from './atoms';
import { AccountDataMessageTypes, channel } from './channels';
import { useCurrentCustomerId } from './hooks';
import { type Features, type RefetchUserParams } from './types';

type UserWithCustomers = User & { customers: Customer[] };

const AUTH_REDIRECT_ATTEMPT_KEY = 'auth_redirect_attempts';
// Allow up to 2 silent re-auth attempts before escalating to /logout.
// /auth_redirect is invisible to the user on success, so 2 attempts is a
// reasonable balance between resilience and breaking redirect loops.
const MAX_AUTH_REDIRECT_ATTEMPTS = 2;

export function AccountContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [currentCustomerId] = useCurrentCustomerId();
  const {
    customers,
    releaseToggles,
    loading: userLoading,
    user,
    refetchUser,
    currentOrgHasDeliveryRelationshipsAsPartner,
    currentUserOrganizationRecordPermissions,
    canAssignAPIKeys,
  } = useUser(currentCustomerId);
  const { hasReleaseToggle } = useReleaseToggles(releaseToggles);
  const { features, loading: featuresLoading } = useFeatures(!currentCustomerId || !user);
  const { customer, setCustomerId } = useCustomer(user);
  const loading = featuresLoading || userLoading;

  const providerValue = useMemo(() => {
    return {
      customer,
      customers,
      features,
      featuresLoading,
      hasReleaseToggle,
      loading,
      setCustomerId,
      // even though this cast may be a lie, the `user` is checked in `useUser`
      // after the query has run. when the `user` is not set (for whatever
      // reason) they will be redirected, so we can safely assume the `user`
      // will be non-null when put in context
      user: user as UserWithCustomers,
      refetchUser,
      currentOrgHasDeliveryRelationshipsAsPartner,
      currentUserOrganizationRecordPermissions,
      canAssignAPIKeys,
    };
  }, [
    customer,
    customers,
    features,
    featuresLoading,
    hasReleaseToggle,
    loading,
    setCustomerId,
    user,
    refetchUser,
    currentOrgHasDeliveryRelationshipsAsPartner,
    currentUserOrganizationRecordPermissions,
    canAssignAPIKeys,
  ]);

  return (
    <AccountContext.Provider value={providerValue}>
      {!customer && loading ? <Spinner /> : children}
    </AccountContext.Provider>
  );
}

const scopes: string[] = [DEPLOY_ENV];

if (CI_MERGE_REQUEST_IID) {
  scopes.push(`horizon-${CI_MERGE_REQUEST_IID}`);
}

function useReleaseToggles(toggles?: string[]) {
  const [, setReleaseToggles] = useAtom(releaseTogglesAtom);

  useEffect(() => {
    if (!toggles) {
      return;
    }

    setReleaseToggles(toggles);
  }, [setReleaseToggles, toggles]);

  const hasReleaseToggle = useAtomCallback(
    useCallback((get, _set, releaseToggle: string) => {
      const releaseToggles = get(releaseTogglesAtom);
      return doesUserHaveReleaseToggle({ releaseToggles }, releaseToggle);
    }, [])
  );

  return { hasReleaseToggle };
}

function useFeatures(skip: boolean) {
  // @todo determine if we actually need to store the features in atom since
  // they never change once loaded
  const [, setFeatures] = useAtom(featuresAtom);
  const { data, loading } = useGetFeatureFlagsQuery({ skip });
  const features = useMemo<Features>(
    () => ({
      AUTONOMOUS_DRONE_UPLOAD: ['stage'].includes(DEPLOY_ENV),
      ...data?.features,
    }),
    [data]
  );

  useEffect(() => {
    if (!loading) {
      setFeatures(features);
    }
  }, [features, loading, setFeatures]);

  return { features, loading };
}

function useUser(currentCustomerId: string | undefined): {
  customers: AtlasGqlOrgFragmentFragment[];
  releaseToggles: string[];
  loading: boolean;
  user: UserWithCustomers | null | undefined;
  refetchUser: (params: RefetchUserParams) => void;
  currentOrgHasDeliveryRelationshipsAsPartner: boolean;
  currentUserOrganizationRecordPermissions: Customer['recordPermissions'];
  canAssignAPIKeys: boolean;
} {
  const message = useMessage();
  const { replace } = useHistory();
  const { resetCache } = useApolloContext();
  const { data, error, loading, refetch } = useUserDetailsQuery({
    skip: !currentCustomerId,
    variables: { scopes },
  });

  // This useMemo handles determining the user data to return. It also handles
  // redirecting the user to the login page if there is an error with the user
  // query.
  const { customerIds, ...content } = useMemo(() => {
    const customerNameSort = (a: { name?: string | null }, b: { name?: string | null }) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    };

    if (!currentCustomerId || (loading && !data?.me)) {
      // Only treat as "initial loading" when there is no data yet. During a
      // refetch (loading=true but data.me already exists), fall through and use
      // the cached data so we don't reset user/customer to undefined.
      return {
        customerIds: [] as string[],
        customers: [] as Customer[],
        loading: true,
        user: undefined,
        releaseToggles: [] as string[],
        currentUserOrganizationRecordPermissions: [],
      };
    }

    if (!loading && !data?.me) {
      // When data is absent but no Apollo error is set, the cache was just
      // cleared (e.g. clearStore() during an org switch via /reset, or
      // observer.complete() suppressing a 401 during OrgMismatchModal's
      // resetStore()). Treat it as a transient loading state so we don't
      // redirect or throw — the Reset screen or a reload will take over.
      if (!error) {
        return {
          customerIds: [] as string[],
          customers: [] as Customer[],
          loading: true,
          user: undefined,
          releaseToggles: [] as string[],
          currentUserOrganizationRecordPermissions: [],
        };
      }

      // to avoid infinite redirects due to invalid data or user, redirect to
      // /auth_redirect to attempt silent re-auth. After MAX_AUTH_REDIRECT_ATTEMPTS
      // consecutive failures we escalate to /logout to break the cycle.
      console.error('Problem retrieving user data: ', { error });
      notifyBugsnag(`Problem retrieving user data: ${error?.message}`);
      const attempts = parseInt(sessionStorage.getItem(AUTH_REDIRECT_ATTEMPT_KEY) ?? '0', 10);
      if (attempts >= MAX_AUTH_REDIRECT_ATTEMPTS) {
        sessionStorage.removeItem(AUTH_REDIRECT_ATTEMPT_KEY);
        replace('/logout');
      } else {
        sessionStorage.setItem(AUTH_REDIRECT_ATTEMPT_KEY, String(attempts + 1));
        replace('/auth_redirect');
      }
      // Throw to abort this memo — replace() is async (navigation hasn't
      // completed yet), so without the throw React would continue evaluating
      // the memo and hit data.me accesses below. The throw also ensures the
      // sessionStorage.removeItem on the success path below is never reached.
      throw new Error('Error retrieving user data.');
    }

    // Only reached when data.me is present (the throw above guards the error path).
    sessionStorage.removeItem(AUTH_REDIRECT_ATTEMPT_KEY);
    const user: User = { ...data.me, roles: [] };
    const releaseToggles = data.me.releaseToggles ?? [];
    const customerIds = data.me.organizations.map(o => o.id);
    const customersWithHorizonRoles = data.me.organizations.filter(
      o => o.roles && o.roles.horizon?.name
    );

    if (!customersWithHorizonRoles?.length) {
      // In theory, you shouldn't be able to get here
      // As in Account Manager you can only clear *all* Horizon roles if you remove the user from the Horizon group,
      // which we already check for and log the user out in Session if they do not have it
      replace('/logout');
    }

    const customers = [...data.me.organizations].sort(customerNameSort);
    const horizonRole = data.userOrganization?.roles?.horizon;
    const currentOrgHasDeliveryRelationshipsAsPartner = data.userOrganization?.hasRelationships;
    const currentUserOrganizationRecordPermissions = data.userOrganization?.recordPermissions ?? [];
    const canAssignAPIKeys = data.userOrganization?.canAssignAPIKeys;

    if (horizonRole) {
      user.roles!.push(horizonRole);
    }

    return {
      customerIds,
      customers,
      loading,
      user: { ...user, customers },
      releaseToggles,
      currentOrgHasDeliveryRelationshipsAsPartner,
      currentUserOrganizationRecordPermissions,
      canAssignAPIKeys,
    };
  }, [currentCustomerId, loading, error, replace, data]);

  const { user } = content;

  // This useEffect handles redirecting the user to the login page if they
  // don't have access to the current customer.
  useEffect(() => {
    if (!currentCustomerId || loading || !user) {
      return;
    }

    // validate that the user has access to the specified customer. when access
    // is not available, reset the user to view their base organization
    if (customerIds.length > 0 && !customerIds.includes(currentCustomerId)) {
      message.error('Access to customer is not allowed. Redirecting...');
      removeItem('customerId');
      resetCache();

      const timeout = setTimeout(() => {
        // since the customerId has been removed from local storage, we are
        // resetting to the default customer.
        // note that we want to use the `replace` off the window.location
        // directly and not the one from `useHistory` to get a full page reload.
        window.location.replace('/reset');
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [customerIds, currentCustomerId, error, loading, message, replace, resetCache, user]);

  // used in situations where we want to update the user data after app load
  // publish = true will publish a message to other open tabs and windows causing them to refetch
  const refetchUser = useCallback(
    ({ publish = false }: RefetchUserParams) => {
      refetch();
      if (publish) {
        channel.postMessage({
          type: AccountDataMessageTypes.USER_DATA_REFETCH,
        });
      }
    },
    [refetch]
  );

  return { ...content, refetchUser };
}

export function useCustomer(user: UserWithCustomers | null | undefined): {
  customer: Customer | null | undefined;
  setCustomerId: (customerId: string, setActive?: boolean) => void;
} {
  const [currentCustomerId] = useCurrentCustomerId();
  const { replace } = useHistory();
  const customerInitialized = useRef<boolean>(false);
  const pendingCustomerId = useRef<string | null>(null);

  const findCustomer = useCallback(
    (id: string | undefined) => (id ? user?.customers?.find(c => c.id === id) : undefined),
    [user?.customers]
  );

  const setCustomerId = useAtomCallback(
    useCallback(
      (_get, set, newCustomerId: string) => {
        /**
         * If `setCustomerId` is called before the user query has resolved, store the requested
         * customer id in a ref and return early.
         * When user resolves, `setCustomerId` is called to initialize application state with
         * that pending id (if it exists), or the current one otherwise.
         *
         * This prevents a race condition where when this function gets called due to loading a
         * url that triggers an org switch (`/o/:orgId/:path`), the user object is not yet
         * available, so the `useEffect` below fires afterward with a stale customer id,
         * effectiveley reverting the org switch.
         */
        if (!customerInitialized.current) {
          pendingCustomerId.current = newCustomerId;
          return;
        }
        const selectedCustomer = findCustomer(newCustomerId);

        if (selectedCustomer?.id !== newCustomerId) {
          console.error('Unable to update selected customer.');
          return;
        }

        set(currentCustomerAtom, newCustomerId);
        set(activeCustomerAtom, newCustomerId);
        setHeapOrganization({ orgId: newCustomerId, orgName: selectedCustomer.name });
        setTitle(selectedCustomer?.name ?? null);
        pendingCustomerId.current = null;
      },
      [findCustomer]
    )
  );

  const customer = useMemo(
    () => findCustomer(currentCustomerId),
    [currentCustomerId, findCustomer]
  );

  useEffect(() => {
    // If the user hasn't resolved yet, don't bother to try and check access to the customer
    if (!user) {
      return;
    }

    // Need to check that the customer exists so we don't collide with the logic to handle non-existant organizations
    const currentCustomer = user?.organizations.find(o => o.id === currentCustomerId);
    const currentCustomerHasHorizonRole = currentCustomer?.roles?.horizon?.name;
    if (currentCustomer && !currentCustomerHasHorizonRole) {
      const baseOrg = user?.organizations.find(o => o.isDefault && o.roles?.horizon?.name);
      if (baseOrg) {
        setCustomerId(baseOrg.id);
        replace('/reset');
      } else {
        replace('/logout');
      }
    }
  }, [currentCustomerId, replace, setCustomerId, user]);

  useEffect(() => {
    if (user) {
      /**
       * If `setCustomerId` was called before `user` was available, `pendingCustomerId`
       * will exist. If so, call `setCustomerId` again with that id. Otherwise, use
       * `currentCustomerId`.
       *
       * This happens when following links with org ids (`/o/:orgId/:path`)
       */
      const customerId = pendingCustomerId.current ?? currentCustomerId;
      pendingCustomerId.current = null;
      if (customerId) {
        // the `currentCustomerId` (in local storage) is initially set in Session,
        // but we still need to sync session storage with local storage plus
        // initialize heap and set the browser title (in `setCustomerId`)
        customerInitialized.current = true;
        setCustomerId(customerId);
      }
    }
    // we only want to initialize the customer on initial user load, so we only
    // need to add the user to the dependency array. adding `currentCustomerId`
    // would cause this to get run each time the current customer ID changes,
    // which we don't want, especially when the user is switching customers.
    // eslint-disable-next-line
  }, [user?.id]);

  return { customer, setCustomerId };
}
